import { useState } from "react";
import {
    Box,
    Drawer,
    IconButton,
    Paper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Menu } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../app/hooks";
import { logout } from "../../features/auth/authSlice";
import { clearStoredSession } from "../../features/auth/storage";
import { logoutFromBackend } from "../../lib/api";
import { useWorkspaceWarmup } from "../../hooks/useWorkspaceWarmup";
import { resolveActiveWorkspaceItem } from "../../routes/moduleNavigation.utils";
import { isCrossCockpitModuleNavigation } from "../../routes/cockpitModuleNavigation";
import { getActiveWorkspaceItem, routePaths } from "../../routes/navigation";
import { prefetchWorkspaceModule } from "../../routes/modulePrefetch";
import AppShellSidebar from "./AppShellSidebar";
import MessagesNotificationHost from "../messages/MessagesNotificationHost";
import PermissionsReloadBanner from "./PermissionsReloadBanner";
import {
    collapsedDrawerWidth,
    drawerWidth,
    sidebarStorageKey,
} from "./appShell.constants";
import { getInitialDesktopCollapsed } from "./appShell.utils";

export default function AppShell() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [mobileOpen, setMobileOpen] = useState(false);

    const [desktopCollapsed, setDesktopCollapsed] = useState(getInitialDesktopCollapsed);

    const activeItem =
        resolveActiveWorkspaceItem(location.pathname, location.search)
        ?? getActiveWorkspaceItem(location.pathname);

    useWorkspaceWarmup();

    const currentDrawerWidth =
        !isMobile && desktopCollapsed ? collapsedDrawerWidth : drawerWidth;
    const drawerPaperWidth = isMobile
        ? "min(304px, calc(100vw - 24px))"
        : currentDrawerWidth;

    const handleNavigate = (path: string) => {
        const targetPath = path.split("?")[0] ?? path;
        const targetQuery = path.includes("?") ? path.slice(path.indexOf("?")) : "";
        const shouldResetCockpitQuery = isCrossCockpitModuleNavigation(location.pathname, targetPath);

        prefetchWorkspaceModule(targetPath);
        navigate(shouldResetCockpitQuery ? targetPath : `${targetPath}${targetQuery}`);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logoutFromBackend();
        } catch {
            // Even if the backend is unavailable, we still clear the local shell state.
        }

        clearStoredSession();
        dispatch(logout());
        navigate(routePaths.login, { replace: true });
    };

    const handleBrandClick = () => {
        if (!isMobile) {
            setDesktopCollapsed((prev) => {
                const next = !prev;
                localStorage.setItem(
                    sidebarStorageKey,
                    next ? "collapsed" : "expanded"
                );
                return next;
            });
        }
    };

    const drawerContent = (
        <AppShellSidebar
            desktopCollapsed={desktopCollapsed}
            isMobile={isMobile}
            pathname={location.pathname}
            search={location.search}
            onBrandClick={handleBrandClick}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            theme={theme}
        />
    );

    return (
        <Box
            sx={{
                display: "flex",
                height: "100dvh",
                maxHeight: "100dvh",
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                bgcolor: "background.default",
            }}
        >
            <MessagesNotificationHost />
            <PermissionsReloadBanner />
            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: drawerPaperWidth,
                        p: 2,
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop Sidebar */}
            <Box
                component="nav"
                sx={{
                    width: { md: currentDrawerWidth },
                    flexShrink: { md: 0 },
                    display: { xs: "none", md: "block" },
                    transition: theme.transitions.create("width", {
                        duration: 250,
                        easing: theme.transitions.easing.easeInOut,
                    }),
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: currentDrawerWidth,
                        height: "100%",
                        borderRadius: 0,
                        borderRight: 1,
                        borderColor: "divider",
                        p: desktopCollapsed ? 1 : 2,
                        boxSizing: "border-box",
                        overflow: "hidden",
                    }}
                >
                    {drawerContent}
                </Paper>
            </Box>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    height: "100%",
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {isMobile && (
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: theme.zIndex.appBar,
                            px: 1.5,
                            py: 1,
                            pt: "max(8px, env(safe-area-inset-top))",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "background.default",
                        }}
                    >
                        <IconButton
                            aria-label="Отвори меню"
                            onClick={() => setMobileOpen(true)}
                            edge="start"
                        >
                            <Menu />
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                ml: 0.5,
                                fontWeight: 800,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {activeItem?.label ?? "CRM"}
                        </Typography>
                    </Box>
                )}
                <Box
                    className="workspace-page app-main-content"
                    sx={{
                        p: { xs: 1, sm: 2, md: 4 },
                        pb: { xs: 2, md: 4 },
                        flex: 1,
                        minWidth: 0,
                        maxWidth: "100%",
                    }}
                >
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}
